import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams, Outlet } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useGroup } from "../context/GroupContext";
import { setActiveGroupSlug } from "../firebaseService";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  SportsTennis,
  EventNote,
  Restaurant,
  EditNote,
  People,
  ConfirmationNumber,
  Assessment,
  Comment,
  Receipt,
  Schedule,
  Checkroom,
  Logout,
  Dashboard,
  Print,
  SettingsApplications,
  AccessTime,
  AdminPanelSettings,
  HowToVote as HowToVoteIcon,
  MenuBook,
  SwapHoriz,
  QrCodeScanner,
} from "@mui/icons-material";

const DRAWER_WIDTH = 260;

// Feature-Key Mapping fuer Nav-Items
const FEATURE_MAP = {
  "court-allocation": "courtAllocation",
  "einteilung": "courtAllocation",
  "all_assignments": "courtAllocation",
  "court_crew_times": "courtAllocation",
  "court-start-times": "courtAllocation",
  "freikarten": "freikarten",
  "essensmarken": "essensmarken",
  "clothing_distribution": "einkleidung",
  "attendance-billing": "billing",
  "comments": "comments",
};

const AppLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { groupSlug } = useParams();
  const { logout, isSuperadmin } = useAuth();
  const { groups, activeGroup, switchGroup, hasFeature, isGroupAllowed } = useGroup();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Sync group from URL param; redirect if the slug isn't allowed for this user
  useEffect(() => {
    if (!groupSlug || !activeGroup) return;
    if (!isGroupAllowed(groupSlug)) {
      // Benutzer hat keinen Zugriff auf diese Gruppe -> zur erlaubten Gruppe umleiten
      const fallback = activeGroup.slug;
      const subPath = location.pathname.replace(`/${groupSlug}`, "").replace(/^\//, "");
      navigate(`/${fallback}${subPath ? `/${subPath}` : ""}`, { replace: true });
      setActiveGroupSlug(fallback);
      return;
    }
    if (groupSlug !== activeGroup.slug) {
      const targetGroup = groups.find((g) => g.slug === groupSlug);
      if (targetGroup) {
        switchGroup(groupSlug);
        setActiveGroupSlug(groupSlug);
      }
    }
  }, [groupSlug, activeGroup, groups, switchGroup, isGroupAllowed, navigate, location.pathname]);

  const memberLabel = activeGroup?.memberLabel || activeGroup?.name || "Mitglieder";
  const basePath = `/${groupSlug || activeGroup?.slug || "ballkinder"}`;

  const navSections = [
    {
      title: "Uebersicht",
      items: [
        { label: "Dashboard", icon: <Dashboard />, to: "", color: "#5b8def" },
        { label: memberLabel, icon: <People />, to: "overview", color: "#29b6f6" },
      ],
    },
    {
      title: "Einteilung",
      items: [
        { label: "Platzeinteilung", icon: <SportsTennis />, to: "court-allocation", color: "#5b8def" },
        { label: "Startzeiten setzen", icon: <AccessTime />, to: "court-start-times", color: "#42a5f5" },
      ],
    },
    {
      title: "Verwaltung",
      items: [
        { label: "Anwesenheit", icon: <EventNote />, to: "schedule", color: "#66bb6a" },
        { label: "Check-In Scanner", icon: <QrCodeScanner />, to: "checkin-scanner", color: "#4caf50" },
        { label: "Essensmarken", icon: <Restaurant />, to: "essensmarken", color: "#ffa726" },
        { label: "Freikarten", icon: <ConfirmationNumber />, to: "freikarten", color: "#ef5350" },
        { label: "Einkleidung", icon: <Checkroom />, to: "clothing_distribution", color: "#7e57c2" },
      ],
    },
    {
      title: "Berichte",
      items: [
        { label: "Anwesenheitsbericht", icon: <Assessment />, to: "report", color: "#26a69a" },
        { label: "Abrechnung", icon: <Receipt />, to: "attendance-billing", color: "#e6c06a" },
        { label: "Kommentare", icon: <Comment />, to: "comments", color: "#8d6e63" },
      ],
    },
    {
      title: "Sonstiges",
      items: [
        { label: "Abstimmungen", icon: <HowToVoteIcon />, to: "polls", color: "#7c4dff" },
        { label: `${activeGroup?.memberSingular || "Mitglieder"}-Seite`, icon: <SettingsApplications />, to: "ballkid-page-settings", color: "#26c6da" },
        ...(isSuperadmin
          ? [{ label: "Admin-Tools", icon: <AdminPanelSettings />, to: "admin", color: "#e53935", superadminOnly: true }]
          : []),
        { label: "Dokumentation", icon: <MenuBook />, to: "docs", color: "#78909c" },
      ],
    },
  ];

  // Nav-Items filtern basierend auf Gruppen-Features
  const filteredSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const requiredFeature = FEATURE_MAP[item.to];
        return !requiredFeature || hasFeature(requiredFeature);
      }),
    }))
    .filter((section) => section.items.length > 0);

  const handleNav = (to) => {
    navigate(`${basePath}/${to}`);
    if (isMobile) setMobileOpen(false);
  };

  const handleGroupSwitch = (slug) => {
    switchGroup(slug);
    setActiveGroupSlug(slug);
    // Get current sub-path and navigate to same page in new group
    const currentSubPath = location.pathname.replace(`/${groupSlug}`, "").replace(/^\//, "");
    navigate(`/${slug}/${currentSubPath}`);
    if (isMobile) setMobileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const isActive = (to) => {
    const fullPath = `${basePath}/${to}`;
    if (to === "") {
      return location.pathname === basePath || location.pathname === `${basePath}/`;
    }
    return location.pathname.startsWith(fullPath);
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Active Group Header */}
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "primary.main", mb: 0.5 }}
        >
          {activeGroup?.name || "Laden..."}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Verwaltung
        </Typography>
      </Box>

      {/* Group Switcher - only if multiple groups */}
      {groups.length > 1 && (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Typography variant="overline" sx={{ px: 0.5, display: "block", color: "text.secondary", fontSize: "0.6rem", letterSpacing: "0.1em", mb: 0.5 }}>
            Gruppe wechseln
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {groups.map((g) => (
              <ListItemButton
                key={g.slug}
                onClick={() => handleGroupSwitch(g.slug)}
                selected={g.slug === activeGroup?.slug}
                sx={{
                  borderRadius: 2,
                  py: 0.75,
                  px: 1.5,
                  backgroundColor: g.slug === activeGroup?.slug ? "rgba(91,141,239,0.15)" : "transparent",
                  border: g.slug === activeGroup?.slug ? "1px solid rgba(91,141,239,0.3)" : "1px solid transparent",
                  "&:hover": { backgroundColor: "rgba(91,141,239,0.08)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <SwapHoriz sx={{ fontSize: 16, color: g.slug === activeGroup?.slug ? "primary.main" : "text.secondary" }} />
                </ListItemIcon>
                <ListItemText
                  primary={g.name}
                  primaryTypographyProps={{
                    fontSize: "0.8rem",
                    fontWeight: g.slug === activeGroup?.slug ? 700 : 400,
                    color: g.slug === activeGroup?.slug ? "primary.main" : "text.secondary",
                  }}
                />
                {g.slug === activeGroup?.slug && (
                  <Chip label="Aktiv" size="small" color="primary" sx={{ height: 20, fontSize: "0.65rem" }} />
                )}
              </ListItemButton>
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* Nav Items */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        {filteredSections.map((section) => (
          <Box key={section.title}>
            <Typography
              variant="overline"
              sx={{ px: 2, pt: 2, pb: 0.5, display: "block", color: "text.secondary", fontSize: "0.65rem", letterSpacing: "0.1em" }}
            >
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => {
                const active = isActive(item.to);
                return (
                  <ListItem key={item.to} disablePadding sx={{ px: 1 }}>
                    <ListItemButton
                      onClick={() => handleNav(item.to)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.25,
                        py: 0.75,
                        backgroundColor: active ? `${item.color}18` : "transparent",
                        "&:hover": { backgroundColor: `${item.color}12` },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: active ? item.color : "text.secondary",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "0.85rem",
                          fontWeight: active ? 600 : 400,
                          color: active ? "text.primary" : "text.secondary",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* Logout */}
      <List sx={{ p: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, py: 1 }}>
            <ListItemIcon sx={{ minWidth: 36, color: "error.main" }}>
              <Logout />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ fontSize: "0.85rem", color: "error.main" }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ color: "text.primary", mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", flex: 1 }}>
              {activeGroup?.name || "Laden..."}
            </Typography>
            {groups.length > 1 && (
              <Chip
                label={activeGroup?.name?.charAt(0) || "?"}
                size="small"
                color="primary"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: DRAWER_WIDTH,
                boxSizing: "border-box",
                backgroundColor: "background.paper",
                borderRight: "1px solid rgba(255,255,255,0.08)",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              "& .MuiDrawer-paper": {
                width: DRAWER_WIDTH,
                boxSizing: "border-box",
                backgroundColor: "background.paper",
                borderRight: "1px solid rgba(255,255,255,0.08)",
              },
            }}
            open
          >
            {drawerContent}
          </Drawer>
        )}
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: { xs: "56px", md: 0 },
          minHeight: "100vh",
          overflow: "auto",
        }}
      >
        <Box key={groupSlug} sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: "auto" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
